/**
 * WP7a / #46615 — chat entry + object-context seeding for the verification shell.
 *
 * The promoted shell is bare-canvas, so the classic UserMenu/DockBar pair is
 * not mounted around `/`. This module is the retained seam:
 *   · tools-menu Chat opens the dock (`excellent:open-chat`) when Copilot is on
 *   · otherwise Chat routes to Settings → Agents → Chat
 *   · "Ask about this" seeds the dock via `excellent:ask-agent`
 *
 * Terminal/stage stay WP7b. Do not import tree.ts from here.
 */

import type { PageId } from "./nav";

export const OPEN_CHAT_EVENT = "excellent:open-chat";
export const ASK_AGENT_EVENT = "excellent:ask-agent";
export const COPILOT_SETTINGS_HREF = "/settings/agents/copilot";

export const CHAT_OBJECT_KINDS = [
  "finding",
  "run",
  "receipt",
  "policy",
  "evidence",
] as const;

export type ChatObjectKind = (typeof CHAT_OBJECT_KINDS)[number];

export type ChatEntryActivation =
  | { type: "event"; name: typeof OPEN_CHAT_EVENT }
  | { type: "navigate"; href: typeof COPILOT_SETTINGS_HREF };

/** Tools-menu Chat: open the dock, or send the operator to enable Copilot. */
export function activateChatEntry(copilotEnabled: boolean): ChatEntryActivation {
  if (copilotEnabled) return { type: "event", name: OPEN_CHAT_EVENT };
  return { type: "navigate", href: COPILOT_SETTINGS_HREF };
}

export function dispatchOpenChat(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT));
}

export function dispatchAskAbout(query: string): void {
  if (typeof window === "undefined") return;
  const trimmed = query.trim();
  if (!trimmed) return;
  window.dispatchEvent(new CustomEvent(ASK_AGENT_EVENT, { detail: { query: trimmed } }));
}

export function activateChatEntryInWindow(copilotEnabled: boolean, navigate: (to: string) => void): void {
  const next = activateChatEntry(copilotEnabled);
  if (next.type === "event") dispatchOpenChat();
  else navigate(next.href);
}

/**
 * Map the shell chrome route onto an ask-about kind. List pages with an id
 * (e.g. `/findings/:id`) are detail surfaces even when the rail row is the list.
 */
export function objectKindFromChrome(page: PageId, id?: string): ChatObjectKind | null {
  if (!id) return null;
  if (page === "finding" || page === "findings") return "finding";
  if (page === "run" || page === "runs") return "run";
  if (page === "receipt" || page === "receipts") return "receipt";
  if (page === "policy" || page === "policies") return "policy";
  if (page === "evidence" || page === "artifact") return "evidence";
  return null;
}

export function seedQueryForObject(input: {
  kind: ChatObjectKind;
  id: string;
  label?: string;
  environment?: "demo" | "real" | null;
  href?: string;
}): string {
  const env =
    input.environment === "real" ? "Real" : input.environment === "demo" ? "Demo" : "current";
  const name = (input.label ?? "").trim() || input.id;
  const path = input.href ? ` at ${input.href}` : "";
  return `Ask about this ${env} ${input.kind} “${name}” (${input.id})${path}.`;
}

export function chatEntryTitle(copilotEnabled: boolean): string {
  return copilotEnabled
    ? "Chat"
    : "Chat is off — enable in Settings → Agents → Chat";
}
